import { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { MagnifyingGlassIcon, SparkleIcon, XIcon } from '@phosphor-icons/react';
import { parseCdnImageOriginUrl, updateAttributesOfCdnImage } from '@admin-panel/utils';
import { Button } from '@admin-panel/ui';
import { PlayfulCatLottie } from '@admin-panel/components';
import { LayoutGroup, motion } from 'motion/react';
import type { LookDTOType } from '../../../schemas/styling';
import { useGetRecommendationsMutation } from '../../../store/stylesSearch/stylesSearchApi';
import { useUpdateLookContentMutation } from '../../../store/stylist/stylistApi';
import {
  normalizeEnrichmentPayload,
  type SourceItem,
  type VisualSearchProduct,
} from '../GeneratedWidgetList';
import { SectionHeading } from '../helpers';
import {
  getProductDepth,
  ProductDepthRail,
  useProductDepthByStyleNumbers,
} from '../productDepth';

type VisualSearchSectionProps = {
  showHeading: boolean;
  lookId?: number;
  visualSearch?: unknown;
  widgets?: unknown;
  isPublished: boolean;
  isTriggeringVisualSearch: boolean;
  onTriggerVisualSearch?: () => void;
  onRefreshVisualSearch?: () => void;
  onVisualSearchUpdated?: (look: LookDTOType) => void;
};

const COMPONENT_TITLES: Record<string, string> = {
  bags: 'Bags to match',
  bag: 'Bags to match',
  jewellery: 'Jewellery to match',
  footwear: 'Footwear to match',
  outfit_completion: 'Pair it With',
  layering: 'Add a Layer',
  accessories: 'Other Accessories',
};

const COMPONENT_PRODUCT_TYPES: Record<string, string> = {
  bags: 'bags',
  bag: 'bags',
  jewellery: 'jewellery',
  footwear: 'footwear',
  outfit_completion: 'outfit_completion',
  layering: 'layering',
  accessories: 'accessories',
};

const getString = (value: unknown) => (typeof value === 'string' ? value.trim() : '');

const buildImageUrl = (value: string, width: number) =>
  value ? updateAttributesOfCdnImage(value, { width, quality: 100 }) || value : '';

const normalizeDeepLink = (value: unknown) => {
  const raw = getString(value);
  if (!raw) {
    return '';
  }

  try {
    const parsed = new URL(raw);
    return `${parsed.pathname.replace(/^\/+/, '')}${parsed.search}`;
  } catch {
    return raw.replace(/^https?:\/\//, '').replace(/^\/+/, '');
  }
};

const normalizeSearchProduct = (value: Record<string, unknown>): VisualSearchProduct | null => {
  const title = getString(value.title ?? value.name);
  const id =
    getString(value.id) || getString(value.product_id) || getString(value.lk_style_number) || title;
  const deepLink = normalizeDeepLink(value.product_link ?? value.deepLink ?? value.deep_link);

  if (!title || !id || !deepLink) {
    return null;
  }

  return {
    id,
    name: title,
    assetUrl: getString(value.default_image ?? value.assetUrl ?? value.asset_url),
    deepLink,
    rank: null,
    lkStyleNumber: getString(value.lk_style_number ?? value.lkStyleNumber),
    ...getProductDepth({
      size_depth_in_lk:
        typeof value.size_depth_in_lk === 'number' ? value.size_depth_in_lk : undefined,
      size_depth_in_lk_or_jit:
        typeof value.size_depth_in_lk_or_jit === 'number'
          ? value.size_depth_in_lk_or_jit
          : undefined,
      variants: Array.isArray(value.variants) ? value.variants : [],
    }),
  };
};

const buildSearchFacets = ({
  productTypeFilters,
  colorFilters,
}: {
  productTypeFilters: Array<string>;
  colorFilters: Array<string>;
}): Record<string, Array<string>> => {
  const selectedFacets: Record<string, Array<string>> = {
    // 'In-Stock': ['true'],
    'Shopify Status': ['ACTIVE'],
  };

  if (productTypeFilters.length > 0) {
    selectedFacets['Product Type'] = productTypeFilters;
  }
  if (colorFilters.length > 0) {
    selectedFacets.Color = colorFilters;
  }

  return selectedFacets;
};

const productIdentifier = (product: VisualSearchProduct) => product.id || product.deepLink;

const normalizeMappedProductsFromSourceItem = (
  item: SourceItem,
  depthByStyleNumber: Record<string, { inventoryDepth: number; jitDepth: number }>,
): Array<VisualSearchProduct> => {
  const mappedImages = item.mappedProductImages || {};
  const orderedStyleNumbers = [
    ...item.mappedLkStyles,
    ...Object.keys(mappedImages).filter(styleNumber => !item.mappedLkStyles.includes(styleNumber)),
  ];

  return orderedStyleNumbers
    .map(styleNumber => {
      const assetUrl = mappedImages[styleNumber] || '';
      if (!styleNumber && !assetUrl) {
        return null;
      }

      const productDepth = styleNumber ? depthByStyleNumber[styleNumber] : undefined;

      return {
        id: styleNumber || assetUrl,
        name: styleNumber || 'Mapped product',
        assetUrl,
        deepLink: '',
        rank: null,
        lkStyleNumber: styleNumber,
        inventoryDepth: productDepth?.inventoryDepth,
        jitDepth: productDepth?.jitDepth,
      } satisfies VisualSearchProduct;
    })
    .filter(Boolean) as Array<VisualSearchProduct>;
};

const buildVisualSearchState = (
  sourceItems: Array<SourceItem>,
  selectedProductsByItem: Record<string, Array<VisualSearchProduct>>,
) =>
  sourceItems
    .map(item => {
      const selectedProducts = selectedProductsByItem[item.itemKey] || [];
      const mappedLkStyles = selectedProducts
        .map(product => getString(product.lkStyleNumber))
        .filter(Boolean);
      const mappedProductImages = Object.fromEntries(
        selectedProducts
          .map(product => [getString(product.lkStyleNumber), getString(product.assetUrl)] as const)
          .filter(([styleNumber, assetUrl]) => styleNumber && assetUrl),
      );
      const productType =
        getString(item.productType) || COMPONENT_PRODUCT_TYPES[item.componentGroup];

      if (!productType) {
        return null;
      }

      return {
        product_type: productType,
        item_key: item.itemKey,
        product_label: item.productLabel,
        product_description: item.productDescription,
        product_type_filters: item.productTypeFilters,
        color_filters: item.colorFilters,
        search_seed: item.searchSeed,
        isolation_notes: item.isolationNotes,
        isolated_image_url: item.isolatedImageUrl,
        mapped_lk_styles: mappedLkStyles,
        mapped_product_images: mappedProductImages,
      };
    })
    .filter(
      (
        item,
      ): item is {
        product_type: string;
        item_key: string;
        product_label: string;
        product_description: string;
        product_type_filters: Array<string>;
        color_filters: Array<string>;
        search_seed: string;
        isolation_notes: string;
        isolated_image_url: string;
        mapped_lk_styles: Array<string>;
        mapped_product_images: Record<string, string>;
      } => Boolean(item),
    );

const serializeSelectedProducts = (
  selectedProductsByItem: Record<string, Array<VisualSearchProduct>>,
) =>
  JSON.stringify(
    Object.keys(selectedProductsByItem)
      .sort()
      .reduce<Record<string, Array<VisualSearchProduct>>>((accumulator, itemKey) => {
        accumulator[itemKey] = selectedProductsByItem[itemKey];
        return accumulator;
      }, {}),
  );

const serializeSourceItems = (sourceItems: Array<SourceItem>) =>
  JSON.stringify(
    sourceItems.map(item => ({
      itemKey: item.itemKey,
      isolatedImageUrl: item.isolatedImageUrl,
      productTypeFilters: item.productTypeFilters,
      colorFilters: item.colorFilters,
    })),
  );

const SourceThumb = ({
  imageUrl,
  alt,
  fallback,
  className = 'h-24 w-20',
}: {
  imageUrl: string;
  alt: string;
  fallback: string;
  className?: string;
}) => (
  <div className={`overflow-hidden rounded-md border border-border/80 bg-muted/30 ${className}`}>
    {imageUrl ? (
      <img src={imageUrl} alt={alt} className="h-full w-full object-cover" />
    ) : (
      <div className="flex h-full w-full items-center justify-center px-2 text-xs text-muted-foreground">
        {fallback}
      </div>
    )}
  </div>
);

const ProductCard = ({
  product,
  isSelected,
  hasSearchResults,
  onToggle,
}: {
  product: VisualSearchProduct;
  isSelected: boolean;
  hasSearchResults: boolean;
  onToggle: () => void;
}) => (
  <div className="group relative w-fit">
    <motion.button
      layout
      // layoutId={`visual-search-product-${productIdentifier(product)}`}
      type="button"
      onClick={onToggle}
      aria-pressed={isSelected}
      aria-label={isSelected ? `Unselect ${product.name}` : `Select ${product.name}`}
      className={[
        'relative block aspect-2/3 h-40 overflow-hidden rounded-md border bg-muted/20 transition',
        isSelected && hasSearchResults
          ? 'border-foreground/80 shadow-lg hover:cursor-pointer'
          : 'border-transparent',
        !isSelected ? 'hover:shadow-md hover:cursor-pointer' : '',
      ].join(' ')}
      title={product.name || product.id}
      transition={{ layout: { type: 'spring', stiffness: 460, damping: 36, mass: 0.7 } }}
    >
      <SourceThumb
        imageUrl={buildImageUrl(product.assetUrl, 320)}
        alt={product.name || product.id}
        fallback={product.lkStyleNumber || product.id || 'No image'}
        className="h-full w-full border-0"
      />

      <ProductDepthRail
        inventoryDepth={product.inventoryDepth}
        jitDepth={product.jitDepth}
      />

      {isSelected ? <span className="pointer-events-none absolute inset-0 bg-black/8" /> : null}
    </motion.button>

    {isSelected ? (
      <Button
        variant="outline"
        color="neutral"
        size="small"
        className={[
          'absolute -right-1 -top-1 z-10 h-6 min-h-0 w-6 min-w-0 rounded-full p-0 opacity-0 shadow-sm transition-opacity',
          'group-hover:opacity-100',
        ].join(' ')}
        onClick={onToggle}
        aria-label={`Unselect ${product.name}`}
      >
        <XIcon size={14} />
      </Button>
    ) : null}
  </div>
);

const SearchProductsTile = ({
  isLoading,
  isDisabled,
  onClick,
}: {
  isLoading: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isDisabled}
    className={[
      ' flex aspect-2/3 h-40 flex-col items-center justify-center rounded-sm border border-dashed border-border/70 bg-muted/20 px-3 text-center transition',
      isLoading
        ? 'hover:cursor-progress'
        : 'hover:border-foreground/40 hover:bg-muted/30 hover:shadow-sm hover:cursor-pointer',
    ].join(' ')}
  >
    <MagnifyingGlassIcon size={24} />
    <span className="mt-2 text-xs font-medium text-foreground">
      {isLoading ? 'Searching...' : 'Search Products'}
    </span>
  </button>
);

const VisualSearchSection = ({
  showHeading,
  lookId,
  visualSearch,
  widgets,
  isPublished,
  isTriggeringVisualSearch,
  onTriggerVisualSearch,
  onRefreshVisualSearch,
  onVisualSearchUpdated,
}: VisualSearchSectionProps) => {
  const visualSearchState = useMemo(() => normalizeEnrichmentPayload(visualSearch), [visualSearch]);
  const widgetsState = useMemo(() => normalizeEnrichmentPayload(widgets), [widgets]);
  const [triggerRecommendations] = useGetRecommendationsMutation();
  const [updateLookContent, { isLoading: isSavingVisualSearch }] = useUpdateLookContentMutation();
  const widgetsStatus = (widgetsState.status || '').toLowerCase();
  const isAwaitingVisualSearch = widgetsStatus === 'building' || widgetsStatus === 'pending';
  const isPollingVisualSearch = isTriggeringVisualSearch;
  const hasGeneratedSourceImages = useMemo(
    () => visualSearchState.sourceItems.some(item => Boolean(item.isolatedImageUrl)),
    [visualSearchState.sourceItems],
  );
  const shouldShowRefreshVisualSearch =
    !isPollingVisualSearch &&
    !hasGeneratedSourceImages &&
    isAwaitingVisualSearch &&
    Boolean(onRefreshVisualSearch);
  const [selectedProductsByItem, setSelectedProductsByItem] = useState<
    Record<string, Array<VisualSearchProduct>>
  >({});
  const [searchResultsByItem, setSearchResultsByItem] = useState<
    Record<string, Array<VisualSearchProduct>>
  >({});
  const [searchingItems, setSearchingItems] = useState<Record<string, boolean>>({});
  const [searchErrors, setSearchErrors] = useState<Record<string, string>>({});
  const [autosaveState, setAutosaveState] = useState<
    'idle' | 'pending' | 'saving' | 'saved' | 'error'
  >('idle');
  const committedSelectionRef = useRef('');
  const sourceItemsSignatureRef = useRef('');
  const sourceItemsSignature = useMemo(
    () => serializeSourceItems(visualSearchState.sourceItems),
    [visualSearchState.sourceItems],
  );
  const selectedStyleNumbers = useMemo(
    () =>
      visualSearchState.sourceItems
        .flatMap(item => [...(item.mappedLkStyles || []), ...Object.keys(item.mappedProductImages || {})])
        .map(String)
        .filter(Boolean),
    [visualSearchState.sourceItems],
  );
  const depthByStyleNumber = useProductDepthByStyleNumbers(selectedStyleNumbers);

  useEffect(() => {
    const nextSelected: Record<string, Array<VisualSearchProduct>> = {};
    visualSearchState.sourceItems.forEach(item => {
      const mappedProducts = normalizeMappedProductsFromSourceItem(item, depthByStyleNumber);
      if (mappedProducts.length > 0) {
        nextSelected[item.itemKey] = mappedProducts;
      }
    });
    setSelectedProductsByItem(nextSelected);
    const didSourceItemsChange = sourceItemsSignatureRef.current !== sourceItemsSignature;
    const validItemKeys = new Set(visualSearchState.sourceItems.map(item => item.itemKey));

    setSearchResultsByItem(previous =>
      didSourceItemsChange
        ? {}
        : Object.fromEntries(
            Object.entries(previous).filter(([itemKey]) => validItemKeys.has(itemKey)),
          ),
    );
    setSearchingItems(previous =>
      didSourceItemsChange
        ? {}
        : Object.fromEntries(
            Object.entries(previous).filter(([itemKey]) => validItemKeys.has(itemKey)),
          ),
    );
    setSearchErrors(previous =>
      didSourceItemsChange
        ? {}
        : Object.fromEntries(
            Object.entries(previous).filter(([itemKey]) => validItemKeys.has(itemKey)),
          ),
    );
    sourceItemsSignatureRef.current = sourceItemsSignature;
    committedSelectionRef.current = serializeSelectedProducts(nextSelected);
    setAutosaveState('idle');
  }, [depthByStyleNumber, sourceItemsSignature, visualSearchState.sourceItems]);

  const serializedSelection = useMemo(
    () => serializeSelectedProducts(selectedProductsByItem),
    [selectedProductsByItem],
  );

  const handleSearchProducts = async (item: SourceItem) => {
    if (!item.isolatedImageUrl) {
      message.warning('Generate the isolated source image first.');
      return;
    }

    const imageUrl = parseCdnImageOriginUrl(item.isolatedImageUrl);

    setSearchingItems(previous => ({ ...previous, [item.itemKey]: true }));
    setSearchErrors(previous => ({ ...previous, [item.itemKey]: '' }));

    try {
      const response = await triggerRecommendations({
        searchType: 'IMAGE' as const,
        imageUrl,
        selectedFacets: buildSearchFacets({
          productTypeFilters: item.productTypeFilters,
          colorFilters: item.colorFilters,
        }),
      }).unwrap();

      const normalizedProducts = (response.products || [])
        .map(product => normalizeSearchProduct(product as unknown as Record<string, unknown>))
        .filter(Boolean) as Array<VisualSearchProduct>;

      setSearchResultsByItem(previous => ({
        ...previous,
        [item.itemKey]: normalizedProducts,
      }));
    } catch (error) {
      console.error('Error searching visual-search products:', error);
      setSearchErrors(previous => ({
        ...previous,
        [item.itemKey]: 'Failed to search products for this source image.',
      }));
    } finally {
      setSearchingItems(previous => ({ ...previous, [item.itemKey]: false }));
    }
  };

  const handleToggleProduct = (itemKey: string, product: VisualSearchProduct) => {
    setSelectedProductsByItem(previous => {
      const currentProducts = previous[itemKey] || [];
      const identifier = productIdentifier(product);
      const exists = currentProducts.some(current => productIdentifier(current) === identifier);

      return {
        ...previous,
        [itemKey]: exists
          ? currentProducts.filter(current => productIdentifier(current) !== identifier)
          : [...currentProducts, product],
      };
    });
  };

  useEffect(() => {
    if (!lookId || !isPublished || !hasGeneratedSourceImages) {
      return;
    }
    if (serializedSelection === committedSelectionRef.current) {
      return;
    }
    if (isSavingVisualSearch) {
      return;
    }

    setAutosaveState('pending');

    const timeoutId = window.setTimeout(async () => {
      try {
        setAutosaveState('saving');
        const updatedLook = await updateLookContent({
          id: lookId,
          data: {
            visual_search: buildVisualSearchState(
              visualSearchState.sourceItems,
              selectedProductsByItem,
            ),
          },
        }).unwrap();

        committedSelectionRef.current = serializedSelection;
        onVisualSearchUpdated?.(updatedLook);
        setAutosaveState('saved');
      } catch (error) {
        console.error('Error saving visual search:', error);
        setAutosaveState('error');
        message.error('Failed to save visual search');
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [
    hasGeneratedSourceImages,
    isPublished,
    isSavingVisualSearch,
    lookId,
    onVisualSearchUpdated,
    updateLookContent,
    selectedProductsByItem,
    serializedSelection,
    visualSearchState.sourceItems,
  ]);

  useEffect(() => {
    if (autosaveState !== 'saved') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setAutosaveState('idle');
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [autosaveState]);

  return (
    <section className="space-y-3">
      {showHeading ? <SectionHeading title="Visual Search" /> : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {widgetsState.status ? <span>{widgetsState.status.replace(/_/g, ' ')}</span> : null}
          {isPollingVisualSearch ? <span>Generating source images</span> : null}
          {autosaveState === 'pending' ? <span>Changes pending</span> : null}
          {autosaveState === 'saving' ? <span>Saving…</span> : null}
          {autosaveState === 'saved' ? <span>Saved</span> : null}
          {autosaveState === 'error' ? <span>Save failed</span> : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!hasGeneratedSourceImages ? (
            shouldShowRefreshVisualSearch ? (
              <Button
                type="button"
                size="small"
                variant="outline"
                isDisabled={isPollingVisualSearch}
                onClick={onRefreshVisualSearch}
              >
                Refresh Status
              </Button>
            ) : (
              <Button
                type="button"
                size="small"
                variant="outline"
                icon={<SparkleIcon size={14} />}
                isLoading={isPollingVisualSearch}
                isDisabled={!isPublished || isPollingVisualSearch || !onTriggerVisualSearch}
                onClick={onTriggerVisualSearch}
              >
                Generate Source Images
              </Button>
            )
          ) : null}
        </div>
      </div>

      {!isPublished ? (
        <p className="m-0 text-xs text-muted-foreground">
          Publish this look before generating source images or saving visual-search selections.
        </p>
      ) : null}

      {widgetsState.buildError ? (
        <p className="m-0 text-xs text-rose-700">{widgetsState.buildError}</p>
      ) : null}

      {isPollingVisualSearch && !hasGeneratedSourceImages ? (
        <div className="flex justify-center py-2 cursor-progress animate-pulse">
          <PlayfulCatLottie width={256} height={'auto'} />
        </div>
      ) : null}

      <div className="space-y-3 pt-3">
        {visualSearchState.sourceItems.length === 0 ? (
          <p className="m-0 text-xs text-muted-foreground">
            No source images have been generated for this look yet.
          </p>
        ) : (
          visualSearchState.sourceItems.map(item => {
            const selectedProducts = selectedProductsByItem[item.itemKey] || [];
            const searchResults = searchResultsByItem[item.itemKey] || [];
            const hasSearchResults = searchResults.length > 0;
            const availableProducts = searchResults.filter(
              product =>
                !selectedProducts.some(
                  current => productIdentifier(current) === productIdentifier(product),
                ),
            );
            const searchError = searchErrors[item.itemKey];
            const isSearching = Boolean(searchingItems[item.itemKey]);
            return (
              <article
                key={item.itemKey}
                className="space-y-3 rounded-md border border-border/70 p-3"
              >
                <div className="grid gap-3 lg:grid-cols-[140px_minmax(0,1fr)]">
                  <div className="space-y-2">
                    <SourceThumb
                      imageUrl={buildImageUrl(item.isolatedImageUrl, 480)}
                      alt={item.productLabel || item.itemKey}
                      fallback={item.productLabel || item.itemKey || 'No image'}
                      className="h-52 aspect-2/3"
                    />
                    {item.productTypeFilters.length > 0 ? (
                      <div className="space-y-2">
                        <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Product Type
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.productTypeFilters.map(value => (
                            <span
                              key={`${item.itemKey}-product-type-${value}`}
                              className="rounded-full border border-foreground/70 bg-foreground px-3 py-1.5 text-sm text-background"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {item.colorFilters.length > 0 ? (
                      <div className="space-y-2">
                        <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Color
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {item.colorFilters.map(value => (
                            <span
                              key={`${item.itemKey}-color-${value}`}
                              className="rounded-full border border-foreground/70 bg-foreground px-3 py-1.5 text-sm text-background"
                            >
                              {value}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="m-0 text-sm font-semibold text-foreground">
                          {COMPONENT_TITLES[item.productType || item.componentGroup] ||
                            item.productType ||
                            item.componentGroup}{' '}
                          | {item.productLabel || item.itemKey}
                        </p>
                        {item.productDescription ? (
                          <p className="m-0 text-sm text-muted-foreground">
                            {item.productDescription}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    {item.errorMessage ? (
                      <p className="m-0 text-xs text-rose-700">{item.errorMessage}</p>
                    ) : null}
                    {searchError ? (
                      <p className="m-0 text-xs text-rose-700">{searchError}</p>
                    ) : null}

                    <LayoutGroup id={`visual-search-item-${item.itemKey}`}>
                      {selectedProducts.length > 0 || item.isolatedImageUrl ? (
                        <div className="flex flex-col gap-2">
                          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Selected Products
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {selectedProducts.map(product => (
                              <ProductCard
                                key={`${item.itemKey}-selected-${productIdentifier(product)}`}
                                product={product}
                                isSelected
                                hasSearchResults={hasSearchResults}
                                onToggle={() => handleToggleProduct(item.itemKey, product)}
                              />
                            ))}
                            <SearchProductsTile
                              isLoading={isSearching}
                              isDisabled={!item.isolatedImageUrl}
                              onClick={() => {
                                if (isSearching) {
                                  return;
                                }
                                handleSearchProducts(item);
                              }}
                            />
                          </div>
                        </div>
                      ) : null}

                      {availableProducts.length > 0 ? (
                        <div className="flex flex-col gap-2">
                          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Search Results
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {availableProducts.map(product => (
                              <ProductCard
                                key={`${item.itemKey}-result-${productIdentifier(product)}`}
                                product={product}
                                isSelected={false}
                                hasSearchResults={hasSearchResults}
                                onToggle={() => handleToggleProduct(item.itemKey, product)}
                              />
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </LayoutGroup>
                  </div>
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );
};

export default VisualSearchSection;
